import { lazy, Suspense, useEffect, useMemo, useState } from 'react'
import { displayTable } from '../utils/tableLabel'
import { Skeleton } from '../components/Skeleton'
import { useSearchParams } from 'react-router-dom'
import { REPORT_SECTIONS } from '../utils/reportSections'
import { useAuth } from '../context/AuthContext'
import { useCosts } from '../hooks/useCosts'
import { margin, soldUnits, unitCostKurus } from '../utils/cost'
import { useOrders } from '../context/OrdersContext'
import { useCatalog } from '../context/CatalogContext'
import { BarList, ShareBar, tl } from '../components/report/ReportCharts'
import { byCategory, byHour, byPayment, byPerson, byProduct } from '../utils/reportStats'
import { Spinner } from '../components/Spinner'
import { fetchOrdersRange, setTicketPrepaid, voidOrder } from '../services/orders'
import { useOpenTickets } from '../context/OpenTicketsContext'
import { formatTRY } from '../utils/money'
import { formatClock, formatDayKey, toDayKey } from '../utils/dayKey'
import { DayPicker } from '../components/pos/DayPicker'
import { ChevronDownIcon } from '../components/icons'
import { ConfirmDialog, type ConfirmSpec } from '../components/pos/ConfirmDialog'
import { PAYMENT_LABELS, type Order, type PaymentMethod } from '../types'
import type { ReceiptKind } from '../components/receipt/Receipt'
import './DayReportPage.css'

// Grafik kütüphanesi (Recharts) yalnız rapor açılınca iniyor: kasa ekranını
// ağırlaştırmasın.
const RangeReport = lazy(() =>
  import('../components/report/RangeReport').then((m) => ({ default: m.RangeReport })),
)
const HourBars = lazy(() =>
  import('../components/report/RangeCharts').then((m) => ({ default: m.HourBars })),
)
const AdvancedReport = lazy(() =>
  import('../components/report/AdvancedReport').then((m) => ({ default: m.AdvancedReport })),
)
const PaymentDonut = lazy(() =>
  import('../components/report/RangeCharts').then((m) => ({ default: m.PaymentDonut })),
)

/** Sipariş listesi süzgeçleri. "Tümü" iptalleri saymıyor, "İptal" ayrı. */
type OrderFilter = 'all' | 'cash' | 'card' | 'meal' | 'credit' | 'open' | 'void'
const ORDER_FILTERS: [OrderFilter, string][] = [
  ['all', 'Tümü'],
  ['cash', 'Nakit'],
  ['card', 'Kart'],
  ['meal', 'Yemek kartı'],
  ['credit', 'Cari'],
  ['open', 'Açık'],
  ['void', 'İptal'],
]
const matchesFilter = (o: Order, f: OrderFilter): boolean => {
  if (f === 'void') return o.status === 'void'
  if (o.status === 'void') return false
  if (f === 'all') return true
  if (f === 'open') return o.status === 'open'
  if (f === 'cash') return o.paymentMethod === 'cash' || (o.split?.cashKurus ?? 0) > 0
  if (f === 'card') return o.paymentMethod === 'card' || (o.split?.cardKurus ?? 0) > 0
  if (f === 'meal') return o.paymentMethod === 'meal'
  return o.paymentMethod === 'credit'
}

/** Raporun dönemi: tek gün (canlı) ya da bir aralık (bir kez okunur). */
type Period = 'day' | '7' | '30' | 'month' | 'custom'
const PERIODS: [Period, string][] = [
  ['day', 'Gün'],
  ['7', '7 gün'],
  ['30', '30 gün'],
  ['month', 'Bu ay'],
  ['custom', 'Özel'],
]

const daysAgo = (n: number) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return toDayKey(d)
}

/**
 * 'paid' leads because it is what the till records now; the rest stay so a day
 * from before the cash/card split was dropped still adds up on this page.
 */
const METHODS: PaymentMethod[] = ['paid', 'cash', 'card', 'meal', 'credit', 'student']

interface Props {
  onPrint: (order: Order, kind?: ReceiptKind) => void
  /** Which day is on screen, 'YYYY-MM-DD'. Owned by the route above. */
  dayKey: string
  onPickDay: (dayKey: string) => void
}

export function DayReportPage({ onPrint, dayKey, onPickDay }: Props) {
  const { tenantId, admin, isOwner, usePrinter } = useAuth()
  // Maliyet ve kar (18 Eki 2026): yalnız patron.
  const { materialMap, costMap } = useCosts(tenantId, isOwner)
  /** Same record, named for where it is: a donation or a tip. */
  const { orders: dayOrders, loading: dayLoading, error } = useOrders()
  const { products, categories } = useCatalog()
  /** Sipariş listesinin süzgeci (kullanıcı, 11 Eki 2026). */
  const [orderFilter, setOrderFilter] = useState<OrderFilter>('all')
  /** Açık adisyonlar, canlı: ara ödeme iptalinde kalanı düzeltmek için. */
  const { openTickets: liveTickets } = useOpenTickets()
  /** Ürün satışları: kategoriye göre süzgeç; '' = hepsi. */
  const [productCat, setProductCat] = useState('')
  const [picking, setPicking] = useState(false)
  /** Özel aralık için tek takvim açık (22 Eki 2026). */
  const [pickingRange, setPickingRange] = useState(false)
  const [ask, setAsk] = useState<ConfirmSpec | null>(null)
  const [search] = useSearchParams()
  const section = REPORT_SECTIONS.find(([id]) => id === search.get('bolum'))
  const [periodState, setPeriod] = useState<Period>('day')
  const period: Period = periodState
  const [customFrom, setCustomFrom] = useState(() => daysAgo(13))
  const [customTo, setCustomTo] = useState(() => toDayKey())
  const range = useMemo(() => {
    const today = toDayKey()
    if (period === '7') return { from: daysAgo(6), to: today }
    if (period === '30') return { from: daysAgo(29), to: today }
    if (period === 'month') return { from: `${today.slice(0, 8)}01`, to: today }
    if (period === 'custom') {
      return customFrom <= customTo
        ? { from: customFrom, to: customTo }
        : { from: customTo, to: customFrom }
    }
    return null
  }, [period, customFrom, customTo])

  /*
   * Alt başlıklarda (ödeme, ürünler, siparişler) da geçmiş dönem seçilebiliyor
   * (kullanıcı, 11 Eki 2026). Gün seçiliyken canlı günün siparişleri; bir
   * aralık seçiliyse o aralık bir kez okunuyor.
   */
  const [rangeOrders, setRangeOrders] = useState<Order[] | null>(null)
  const [rangeError, setRangeError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const advanced = section?.[0] === 'gelismis'
  const rangeFrom = section && !advanced && range ? range.from : null
  const rangeTo = section && !advanced && range ? range.to : null
  useEffect(() => {
    if (!tenantId || !rangeFrom || !rangeTo) {
      setRangeOrders(null)
      return
    }
    let cancelled = false
    setRangeOrders(null)
    setRangeError(null)
    fetchOrdersRange(tenantId, rangeFrom, rangeTo)
      .then((list) => !cancelled && setRangeOrders([...list].reverse()))
      .catch((e: Error) => !cancelled && setRangeError(e.message || 'Rapor okunamadı.'))
    return () => {
      cancelled = true
    }
  }, [tenantId, rangeFrom, rangeTo, reloadKey])
  const inRange = rangeFrom !== null
  // Gelişmiş Rapor tek günde anlamsız: açılınca dönem 30 güne geçiyor.
  useEffect(() => {
    if (advanced && periodState === 'day') setPeriod('30')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanced])
  const orders = inRange ? (rangeOrders ?? []) : dayOrders
  const loading = inRange ? rangeOrders === null && !rangeError : dayLoading

  const stats = useMemo(() => {
    // Only settled sales count as revenue. An open ticket is food already
    // served but not yet paid for, so folding it into the day's takings would
    // overstate the till before anyone had handed money over.
    const live = orders.filter((o) => o.status === 'completed')
    const openTickets = orders.filter((o) => o.status === 'open')

    // A split order contributes to both cash and card rather than sitting in
    // a column of its own, otherwise the cash drawer never reconciles.
    const byMethod = METHODS.map((method) => {
      const rows = live.filter((o) => o.paymentMethod === method)
      let totalKurus = rows.reduce((sum, o) => sum + o.totalKurus, 0)
      let count = rows.length

      for (const o of live) {
        if (!o.split) continue
        const part =
          method === 'cash' ? o.split.cashKurus : method === 'card' ? o.split.cardKurus : 0
        if (part > 0) {
          totalKurus += part
          count += 1
        }
      }

      return { method, count, totalKurus }
    })

    const categoryName = (pid: string) => {
      const cid = products.find((x) => x.id === pid)?.categoryId
      return categories.find((c) => c.id === cid)?.name ?? 'Diğer'
    }
    const productTotals = new Map<
      string,
      {
        productId: string
        name: string
        category: string
        qty: number
        totalKurus: number
        /** Maliyete giren miktar: tartılıda net kg, diğerlerinde adet. */
        units: number
        byWeight: boolean
      }
    >()
    for (const order of live) {
      for (const line of order.lines) {
        const prev = productTotals.get(line.productId)
        productTotals.set(line.productId, {
          productId: line.productId,
          name: line.name,
          category: categoryName(line.productId),
          qty: (prev?.qty ?? 0) + line.qty,
          totalKurus: (prev?.totalKurus ?? 0) + line.lineTotalKurus,
          units: (prev?.units ?? 0) + soldUnits(line),
          byWeight: (prev?.byWeight ?? false) || Boolean(line.weigh?.plates?.length),
        })
      }
    }

    return {
      // Ara ödemeler (partialOf) sipariş sayılmıyor (22 Eki 2026).
      orderCount: live.filter((o) => !o.partialOf).length,
      itemCount: live.reduce((sum, o) => sum + o.itemCount, 0),
      totalKurus: live.reduce((sum, o) => sum + o.totalKurus, 0),
      discountKurus: live.reduce((sum, o) => sum + (o.discountKurus ?? 0), 0),
      campaignKurus: live.reduce((sum, o) => sum + (o.campaigns ?? []).reduce((t, c) => t + c.kurus, 0), 0),
      campaignCount: live.filter((o) => o.campaigns?.length).length,
      /**
       * Money left on top of bills, kept out of totalKurus on purpose: no
       * product stands behind it, so folding it into takings would overstate
       * what the menu sold. The drawer holds both, the two lines together
       * are what reconciles against it.
       */
      bahsisKurus: live.reduce((sum, o) => sum + (o.bahsisKurus ?? 0), 0),
      bahsisCashKurus: live.reduce(
        (sum, o) => sum + (o.bahsisPaymentMethod === 'cash' ? (o.bahsisKurus ?? 0) : 0),
        0,
      ),
      bahsisCardKurus: live.reduce(
        (sum, o) => sum + (o.bahsisPaymentMethod === 'card' ? (o.bahsisKurus ?? 0) : 0),
        0,
      ),
      openCount: openTickets.length,
      openKurus: openTickets.reduce((sum, o) => sum + o.totalKurus, 0),
      byMethod,
      topProducts: [...productTotals.values()].sort((a, b) => b.qty - a.qty),
      // Grafikler: kişi, saat, kategori, ürün. Yalnızca ödenmiş siparişler.
      people: byPerson(live),
      hours: byHour(live),
      payments: byPayment(live),
      cats: byCategory(live, (pid) => {
        const cid = products.find((x) => x.id === pid)?.categoryId
        return categories.find((c) => c.id === cid)?.name
      }),
      products: byProduct(live).slice(0, 8),
    }
  }, [orders, products, categories])

  const onVoid = (order: Order) => {
    if (!tenantId || !admin) return
    // Ara ödeme iptal ediliyorsa ve adisyon hala açıksa, adisyondaki "ödenen"
    // de düşüyor: yoksa o tutar hiç tahsil edilmemiş olurdu (22 Eki 2026).
    const ticket = order.partialOf ? liveTickets.find((t) => t.id === order.partialOf) : undefined
    setAsk({
      title: `${order.receiptNo} numaralı fiş iptal edilsin mi?`,
      body:
        `Tutar: ${formatTRY(order.totalKurus)}. Bu işlem geri alınamaz.` +
        (ticket ? ` ${displayTable(ticket.tableName) ?? 'Adisyon'} açık; bu tutar kalana geri eklenir.` : ''),
      confirmLabel: 'İptal et',
      danger: true,
      onConfirm: () =>
        void voidOrder(tenantId, order.id, admin.uid).then(() => {
          if (ticket) {
            setTicketPrepaid(tenantId, ticket.id, Math.max(0, (ticket.prepaidKurus ?? 0) - order.totalKurus))
          }
          // Aralık bir kez okundu: iptal görünsün diye yeniden okunuyor.
          if (inRange) setReloadKey((k) => k + 1)
        }),
    })
  }

  // Sayfanın içinde, ekranı kaplamadan: tam ekran yükleme, Rapor'a her
  // basışta uygulama yeniden açılıyormuş gibi görünüyordu (kullanıcı, 11 Eki
  // 2026).
  if (loading) {
    return (
      <div className="rep">
        <Skeleton kind="report" label="Rapor yükleniyor" />
      </div>
    )
  }
  if (error || rangeError)
    return (
      <div className="rep">
        <p className="rep__error">{error ?? rangeError}</p>
      </div>
    )

  const visible = orders.filter((o) => matchesFilter(o, orderFilter))
  const filterCount = (f: OrderFilter) => orders.filter((o) => matchesFilter(o, f)).length
  const productCats = [...new Set(stats.topProducts.map((p) => p.category))].sort((a, b) =>
    a.localeCompare(b, 'tr'),
  )
  const shownProducts = productCat
    ? stats.topProducts.filter((p) => p.category === productCat)
    : stats.topProducts

  /** Ürünün dönemdeki maliyeti ve karı; maliyeti girilmemişse null. */
  const profitOf = (p: (typeof shownProducts)[number]) => {
    const unit = unitCostKurus(costMap.get(p.productId), materialMap)
    if (unit === null) return null
    const costKurus = Math.round(unit * p.units)
    return { costKurus, ...margin(p.totalKurus, costKurus) }
  }
  const showProfit = isOwner && shownProducts.some((p) => costMap.has(p.productId))
  const profitTotal = showProfit
    ? shownProducts.reduce(
        (acc, p) => {
          const r = profitOf(p)
          if (!r) return acc
          return { sales: acc.sales + p.totalKurus, cost: acc.cost + r.costKurus, n: acc.n + 1 }
        },
        { sales: 0, cost: 0, n: 0 },
      )
    : null

  const productsBlock = (
    <>
      {productCats.length > 1 && (
        <div className="rep__chips" role="group" aria-label="Kategori">
          <button
            type="button"
            className={productCat === '' ? 'is-on' : ''}
            onClick={() => {
              setProductCat('')
            }}
          >
            Tümü
          </button>
          {productCats.map((c) => (
            <button
              key={c}
              type="button"
              className={productCat === c ? 'is-on' : ''}
              onClick={() => {
                setProductCat(c)
              }}
            >
              {c}
            </button>
          ))}
        </div>
      )}
      {shownProducts.length === 0 ? (
        <p className="rep__empty">{inRange ? 'Bu dönemde satış yok.' : 'Bugün satış yok.'}</p>
      ) : (
        <>
        <table className="rep__table">
          <tbody>
            {shownProducts.map((p) => {
              const r = showProfit ? profitOf(p) : null
              return (
                <tr key={p.name}>
                  <td>{p.name}</td>
                  <td className="num">
                    {p.byWeight
                      ? `${(Math.round(p.units * 1000) / 1000).toLocaleString('tr-TR')} kg`
                      : `${p.qty} adet`}
                  </td>
                  <td className="num strong">{formatTRY(p.totalKurus)}</td>
                  {showProfit && (
                    <td
                      className={`num rep__profit ${r && r.profitKurus < 0 ? 'is-neg' : ''}`}
                      title={r ? `Maliyet ${formatTRY(r.costKurus)}` : 'Maliyet girilmemiş'}
                    >
                      {r ? (
                        <>
                          Kar {formatTRY(r.profitKurus)}
                          {r.pct !== null && <small> %{Math.round(r.pct)}</small>}
                        </>
                      ) : (
                        <small>maliyet yok</small>
                      )}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
        {/* Maliyeti girilmiş ürünlerin toplamı; girilmemişler sayılmıyor. */}
        {profitTotal && profitTotal.n > 0 && (() => {
          const m = margin(profitTotal.sales, profitTotal.cost)
          return (
            <p className="rep__profit-sum">
              Maliyeti girilmiş {profitTotal.n} üründe: satış <b>{formatTRY(profitTotal.sales)}</b>, maliyet{' '}
              <b>{formatTRY(profitTotal.cost)}</b>, kar{' '}
              <b className={m.profitKurus < 0 ? 'is-neg' : 'is-pos'}>
                {formatTRY(m.profitKurus)}
                {m.pct !== null && ` (%${Math.round(m.pct)})`}
              </b>
            </p>
          )
        })()}
        </>
      )}
    </>
  )

  const ordersBlock = (
    <>
      <div className="rep__chips" role="group" aria-label="Süzgeç">
        {ORDER_FILTERS.map(([f, label]) => {
          const n = filterCount(f)
          if (n === 0 && f !== 'all' && f !== orderFilter) return null
          return (
            <button
              key={f}
              type="button"
              className={orderFilter === f ? 'is-on' : ''}
              onClick={() => {
                setOrderFilter(f)
              }}
            >
              {label}
              <b>{n}</b>
            </button>
          )
        })}
      </div>
      {visible.length === 0 ? (
        <p className="rep__empty">Bu süzgeçte sipariş yok.</p>
      ) : (
        <table className="rep__table rep__table--orders">
          <thead>
            <tr>
              <th>Fiş</th>
              <th>{inRange ? 'Tarih' : 'Saat'}</th>
              <th>Ödeme</th>
              <th className="num">Tutar</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((o) => (
              <tr key={o.id} className={o.status === 'void' ? 'is-void' : ''}>
                <td className="mono">
                  {o.receiptNo}
                  {o.pending && (
                    <span className="rep__pending" title="Henüz kaydedilmedi, cihazda bekliyor">
                      ●
                    </span>
                  )}
                </td>
                <td className="mono">
                  {inRange && (
                    <span className="rep__date">{formatDayKey(o.dayKey).slice(0, 5)} </span>
                  )}
                  {formatClock(o.createdAtLocal)}
                </td>
                <td>
                  {o.paymentMethod ? PAYMENT_LABELS[o.paymentMethod] : 'Açık adisyon'}
                  {o.mealCard && `, ${o.mealCard}`}
                  {o.note && <span className="rep__note">, {o.note}</span>}
                  {o.prepaidKurus ? (
                    <span className="rep__note">, önce {formatTRY(o.prepaidKurus)} ara ödeme alındı</span>
                  ) : null}
                </td>
                <td className="num strong">{formatTRY(o.totalKurus)}</td>
                <td className="rep__actions">
                  {usePrinter && (
                    <button type="button" onClick={() => onPrint(o, 'cash')}>
                      Yazdır
                    </button>
                  )}
                  {o.status !== 'void' && (
                    <button type="button" className="is-danger" onClick={() => onVoid(o)}>
                      İptal
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )

  return (
    <div className="rep">
      {picking && tenantId && (
        <DayPicker
          tenantId={tenantId}
          value={dayKey}
          onPick={(d) => {
            onPickDay(d)
            setPicking(false)
          }}
          onClose={() => setPicking(false)}
        />
      )}
      {pickingRange && tenantId && (
        <DayPicker
          tenantId={tenantId}
          value={customTo}
          onPick={() => {}}
          range={{ from: customFrom, to: customTo }}
          onPickRange={(from, to) => {
            setCustomFrom(from)
            setCustomTo(to)
            setPickingRange(false)
          }}
          onClose={() => setPickingRange(false)}
        />
      )}
      <header className="rep__head">
        <h1>{section ? section[1] : 'Rapor'}</h1>
        <div className="rep__periods" role="tablist" aria-label="Dönem">
          {PERIODS.map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={period === id}
              className={period === id ? 'is-on' : ''}
              onClick={() => {
                setPeriod(id)
                // "Özel": takvim hemen açılsın, iki basışla aralık seçilsin.
                if (id === 'custom') setPickingRange(true)
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="rep__sub">
        {period === 'day' && (
          <>
            {/* The date is the button: it is the thing on this screen most
                worth changing. */}
            <button type="button" className="rep__day" onClick={() => setPicking(true)}>
              {formatDayKey(dayKey)}
              <ChevronDownIcon />
            </button>
          </>
        )}
        {period === 'custom' && (
          /* Tek takvim (kullanıcı, 22 Eki 2026): iki ayrı tarih kutusu yerine
             gün düğmesi gibi aralığın kendisi; basınca takvim açılıyor. */
          <button type="button" className="rep__day" onClick={() => setPickingRange(true)}>
            {customFrom === customTo
              ? formatDayKey(customFrom)
              : `${formatDayKey(customFrom)} - ${formatDayKey(customTo)}`}
            <ChevronDownIcon />
          </button>
        )}
        {range && period !== 'custom' && (
          <span className="rep__range">
            {formatDayKey(range.from)} - {formatDayKey(range.to)}
          </span>
        )}
      </div>

      {range && tenantId && !section && (
        <Suspense fallback={<Spinner label="Grafikler yükleniyor…" />}>
          <RangeReport
            tenantId={tenantId}
            from={range.from}
            to={range.to}
          />
        </Suspense>
      )}

      {section && (
        <div className={advanced ? 'rep__adv' : 'rep__detail'}>
          {advanced && tenantId && (
            <Suspense fallback={<Spinner label="Gelişmiş rapor yükleniyor…" />}>
              <AdvancedReport
                tenantId={tenantId}
                from={range ? range.from : dayKey}
                to={range ? range.to : dayKey}
              />
            </Suspense>
          )}
          {section[0] === 'urunler' && productsBlock}
          {section[0] === 'siparisler' && ordersBlock}
        </div>
      )}

      {!range && !section && (
        <>
          <div className="rep__cards">
            <div className="card card--hero">
              <span>Toplam Ciro</span>
              <strong>{formatTRY(stats.totalKurus)}</strong>
            </div>
            <div className="card">
              <span>Sipariş</span>
              <strong>{stats.orderCount}</strong>
            </div>
            <div className="card">
              <span>Ürün Adedi</span>
              <strong>{stats.itemCount}</strong>
            </div>
            {stats.orderCount > 0 && (
              <div className="card">
                <span>Ortalama Fiş</span>
                <strong>{tl(stats.totalKurus / stats.orderCount)}</strong>
              </div>
            )}
            {stats.discountKurus > 0 && (
              <div className="card">
                <span>İndirimli</span>
                <strong>−{formatTRY(stats.discountKurus)}</strong>
              </div>
            )}
            {stats.campaignKurus > 0 && (
              <div className="card">
                <span>Kampanya ({stats.campaignCount})</span>
                <strong>−{formatTRY(stats.campaignKurus)}</strong>
              </div>
            )}
            {stats.bahsisKurus > 0 && (
              <div className="card">
                <span>Bahşiş</span>
                <strong>{formatTRY(stats.bahsisKurus)}</strong>
              </div>
            )}
            {stats.openCount > 0 && (
              <div className="card card--open">
                <span>Açık Adisyon ({stats.openCount})</span>
                <strong>{formatTRY(stats.openKurus)}</strong>
              </div>
            )}
          </div>

          {stats.orderCount > 0 && (
            <div className="rep__charts">
              <section className="rep__section rep__chart rep__chart--wide rep__panel">
                <h2>Saatlik Satış</h2>
                <Suspense fallback={null}>
                  <HourBars data={stats.hours} />
                </Suspense>
              </section>

              <section className="rep__section rep__chart rep__panel">
                <h2>Ödeme Yöntemi</h2>
                <Suspense fallback={null}>
                  <PaymentDonut data={stats.payments} />
                </Suspense>
              </section>

              <section className="rep__section rep__chart rep__panel">
                <h2>Kategoriler</h2>
                <ShareBar data={stats.cats} />
              </section>

              <section className="rep__section rep__chart rep__panel">
                <h2>Personel</h2>
                <BarList
                  tone="blue"
                  rows={stats.people.map((p) => ({
                    key: p.name,
                    label: p.name,
                    value: p.kurus,
                    valueText: tl(p.kurus),
                    sub: `${p.orders} sipariş, ${p.items} ürün, fiş başı ${tl(p.kurus / Math.max(p.orders, 1))}`,
                  }))}
                />
              </section>

              <section className="rep__section rep__chart rep__panel">
                <h2>En Çok Satanlar</h2>
                <BarList
                  tone="green"
                  rows={stats.products.map((p) => ({
                    key: p.name,
                    label: p.name,
                    value: p.qty,
                    valueText: `${p.qty} adet`,
                    sub: tl(p.kurus),
                  }))}
                />
              </section>
            </div>
          )}
        </>
      )}

      {ask && <ConfirmDialog {...ask} onClose={() => setAsk(null)} />}
    </div>
  )
}
